import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";
import SlidingLabelText from "./SlidingLabelText";

const cx = (...p: (string | false | undefined | null)[]) => p.filter(Boolean).join(" ");

export type PillButtonVariant = "primary" | "outline" | "accent" | "dark" | "gold";

type Shared = {
  variant?: PillButtonVariant;
  className?: string;
  /** Default label text only (no icon). */
  label: ReactNode;
  /** Hover line; defaults to `label` with hover styling. */
  labelHover?: ReactNode;
  /** Static icon (e.g. arrow), not part of the sliding label. */
  icon?: ReactNode;
};

export type PillButtonLinkProps = Shared &
  Omit<LinkProps, "to" | "className" | "children"> & {
    to: string;
    href?: never;
    children?: never;
  };

export type PillButtonAnchorProps = Shared &
  Omit<ComponentPropsWithoutRef<"a">, "href" | "className" | "children"> & {
    href: string;
    to?: never;
    children?: never;
  };

export type PillButtonNativeProps = Shared &
  Omit<ComponentPropsWithoutRef<"button">, "type" | "className" | "children"> & {
    to?: never;
    href?: never;
    type?: "button" | "submit";
    children?: never;
  };

export type PillButtonProps = PillButtonLinkProps | PillButtonAnchorProps | PillButtonNativeProps;

function textColorNormal(variant: PillButtonVariant) {
  if (variant === "gold") return "text-gold";
  return variant === "primary" ? "text-bg-dark" : "text-white";
}

function SlidingLabel({
  variant,
  label,
  labelHover,
}: {
  variant: PillButtonVariant;
  label: ReactNode;
  labelHover?: ReactNode;
}) {
  const normalClass = textColorNormal(variant);
  const hoverClass = variant === "gold" ? "text-bg-dark" : "text-white";

  return (
    <SlidingLabelText line1ClassName={normalClass} line2ClassName={hoverClass} line2={labelHover}>
      {label}
    </SlidingLabelText>
  );
}

/** Pill CTA with sliding label, static icon, and a slight scale interaction. */
export default function PillButton(props: PillButtonProps) {
  const { variant = "primary", className, label, labelHover, icon } = props;

  const shell = cx(
    "cta-button group relative inline-flex shrink-0 items-center justify-center rounded-full border px-5 py-2 text-center",
    "transition-transform duration-200 ease-out will-change-transform",
    /* Self hover: must use `hover:` — `group-hover` only targets descendants, not the .group element itself */
    "hover:scale-[0.97] motion-reduce:hover:scale-100",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
    "sm:px-6",
    variant === "primary" &&
      "border-transparent bg-[#f5f3ed] shadow-sm hover:bg-cta-hover hover:shadow-md hover:shadow-cta-hover/25",
    variant === "outline" &&
      "border border-white/25 bg-transparent hover:border-transparent hover:bg-cta-hover hover:shadow-md hover:shadow-cta-hover/30",
    variant === "accent" &&
      "relative overflow-hidden border-transparent bg-cta-hover shadow-lg shadow-orange/20 before:pointer-events-none before:absolute before:inset-0 before:z-0 before:rounded-full before:bg-linear-to-r before:from-orange before:to-orange-dark before:transition-none hover:before:opacity-0 hover:shadow-md hover:shadow-cta-hover/30",
    variant === "dark" &&
      "border border-white/20 bg-[#121212] shadow-none hover:border-transparent hover:bg-cta-hover hover:shadow-md hover:shadow-cta-hover/25",
    variant === "gold" &&
      "border-gold/80 bg-transparent shadow-[0_0_18px_rgba(216,170,66,0.16)] hover:border-gold hover:bg-gold hover:shadow-md hover:shadow-gold/30",
    className
  );

  const iconWrap = cx(
    "shrink-0",
    variant === "primary" && "text-bg-dark group-hover:text-white",
    variant === "gold" && "text-gold group-hover:text-bg-dark",
    variant !== "primary" && variant !== "gold" && "text-white"
  );

  const inner = (
    <span className="relative z-10 inline-flex items-center gap-1.5">
      <SlidingLabel variant={variant} label={label} labelHover={labelHover} />
      {icon ? <span className={iconWrap}>{icon}</span> : null}
    </span>
  );

  if ("to" in props && props.to) {
    const { to, variant: _v, className: _c, label: _l, labelHover: _lh, icon: _i, ...linkRest } =
      props as PillButtonLinkProps;
    return (
      <Link to={to} className={shell} {...linkRest}>
        {inner}
      </Link>
    );
  }

  if ("href" in props && props.href) {
    const { href, variant: _v, className: _c, label: _l, labelHover: _lh, icon: _i, ...aRest } =
      props as PillButtonAnchorProps;
    return (
      <a href={href} className={shell} {...aRest}>
        {inner}
      </a>
    );
  }

  const native = props as PillButtonNativeProps;
  const {
    type: btnType = "button",
    disabled,
    variant: _v,
    className: _c,
    label: _l,
    labelHover: _lh,
    icon: _i,
    ...btnRest
  } = native;
  const resolvedType: "button" | "submit" = btnType === "submit" ? "submit" : "button";
  const shellBtn = cx(shell, disabled && "pointer-events-none opacity-50");
  return (
    <button type={resolvedType} disabled={disabled} className={shellBtn} {...btnRest}>
      {inner}
    </button>
  );
}
